import type { AlertCondition, AlertEvent, AlertMetric, AlertRule, ApplyTopicResultWithEstimate, BlindOverviewData, Brand, BrandCohort, BrandCohortWithMembers, BrandHealthTracking, Prompt, PromptMode, Comparison, ComparisonResult, CohortSnapshotData, SnapshotReportSummary, DashboardData, FeedItem, MetricBucket, MetricPoint, MetricSummary, PromptType, QueryMode, ResolvedTopicWithEstimate, RollupPeriodType, RollupReport, RollupReportSummary, RollupSchedule, RunComparisonResponse, RunDiff, ScheduleCadence, ScheduleWithStudy, SentimentExplanation, Subtopic, Topic, TopicCompetitorBrand, TopicDetailData, TopicEstimate, TopicKind, TopicListItem, TopicOverviewData, TopicSnapshotData, TopicSummary, TournamentResult, Tournament, Study, StudyDetail, StudyRun, StudyType, TrackingPlan, TrackingPlanEstimate, TrackingStatus, Workspace, Report, ReportDetail } from "../../types";
import type { CompareResultView } from "../../compareReadModel";

// Global current workspace ID — set by the workspace store.
// All workspace-scoped API calls auto-include X-Workspace-Id header.
let currentWorkspaceId = 1;

/**
 * Sets the workspace ID used by workspace-scoped API requests.
 *
 * @param id Active workspace ID.
 * @return void
 */
export function setCurrentWorkspaceId(id: number): void {
  currentWorkspaceId = id;
}

/**
 * Gets the active workspace request headers.
 *
 * @return Headers for workspace-scoped requests.
 */
function wsHeaders(): HeadersInit {
  return { "X-Workspace-Id": String(currentWorkspaceId) };
}

/**
 * Gets JSON headers with the active workspace context.
 *
 * @return Headers for workspace-scoped JSON requests.
 */
function wsJsonHeaders(): HeadersInit {
  return { "Content-Type": "application/json", "X-Workspace-Id": String(currentWorkspaceId) };
}

/**
 * The comparison LIST shape. `/api/comparisons` omits `raw_response` and
 * `llm_metadata` — 43 MB of blobs no list column renders. The detail routes
 * (`comparisons.get` / `getRich`) still return the full `ComparisonResult`.
 */
export type ComparisonListItem = Omit<ComparisonResult, "raw_response" | "llm_metadata">;

export interface Model {
  id: string;
  name: string;
  /**
   * Flagged on the routed families (`family:chatgpt`, …) that Prism preselects.
   * Concrete catalog entries carry no default — pinning one is opt-in.
   */
  default?: boolean;
  /** True for a routed family entry; `/api/models` lists these before the catalog. */
  family?: boolean;
  /** Family entries only: the concrete model this family currently routes to. */
  resolves_to?: string;
  /** Family entries only: display name for `resolves_to` (falls back to the raw id). */
  resolved_name?: string;
}

export interface BrandStat {
  brand_id: number;
  brand_name: string;
  wins: number;
  total_comparisons?: number;
}

export interface RunGroupResult {
  brand_id: number;
  brand_name: string;
  avg_scores: Record<string, number>;
  min_scores: Record<string, number>;
  max_scores: Record<string, number>;
  run_count: number;
  wins: number;
}

export interface TrendEntry {
  date: string;
  model: string;
  prompt_name: string;
  scores: Record<string, number>;
  winner: string | null;
}

export interface SentimentExplanationParams {
  brandId: number;
  range?: string;
  competitorBrandId?: number;
  from?: string;
  to?: string;
  model?: string;
  promptId?: number;
  promptIds?: number[];
}

// Optional callback invoked when the API returns 401 (session expired or invalid)
let onUnauthorized: (() => void) | null = null;

/**
 * Sets the callback invoked when an API response is unauthorized.
 *
 * @param handler Callback to run on a 401 response.
 * @return void
 */
export function setUnauthorizedHandler(handler: () => void): void {
  onUnauthorized = handler;
}

/**
 * Parses a JSON API response and throws normalized API errors.
 *
 * @param res Fetch response.
 * @return Parsed JSON response body.
 * @throws Error when the response is unauthorized or not ok.
 */
async function json<T>(res: Response): Promise<T> {
  if (res.status === 401) {
    onUnauthorized?.();
    const err = await res.json().catch(() => ({ error: 'Unauthorized' }));
    throw new Error(err.error || 'Unauthorized');
  }
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error || res.statusText);
  }
  return res.json();
}

/**
 * Parses a binary API response and throws normalized API errors.
 *
 * @param res Fetch response.
 * @return Response body as a Blob.
 * @throws Error when the response is unauthorized or not ok.
 */
async function blob(res: Response): Promise<Blob> {
  if (res.status === 401) {
    onUnauthorized?.();
    const err = await res.json().catch(() => ({ error: 'Unauthorized' }));
    throw new Error(err.error || 'Unauthorized');
  }
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error || res.statusText);
  }
  return res.blob();
}

/**
 * Adds a serialized query parameter when a value is present.
 *
 * @param query Query string builder.
 * @param key API query parameter key.
 * @param value Candidate value to serialize.
 * @return void
 */
function setPresentQueryParam(query: URLSearchParams, key: string, value?: string | number): void {
  if (value === undefined || value === "") return;
  query.set(key, String(value));
}

/**
 * Builds the sentiment explanation endpoint URL from camelCase client params.
 *
 * @param params Sentiment explanation client parameters.
 * @return Relative API URL with serialized query parameters.
 */
function buildSentimentExplanationUrl(params: SentimentExplanationParams): string {
  const query = new URLSearchParams();
  query.set("brand_id", String(params.brandId));
  setPresentQueryParam(query, "range", params.range);
  setPresentQueryParam(query, "competitor_brand_id", params.competitorBrandId);
  setPresentQueryParam(query, "from", params.from);
  setPresentQueryParam(query, "to", params.to);
  setPresentQueryParam(query, "model", params.model);
  setPresentQueryParam(query, "prompt_id", params.promptId);
  if (params.promptIds && params.promptIds.length > 0) query.set("prompt_ids", params.promptIds.join(","));

  return `/api/metrics/sentiment-explanation?${query.toString()}`;
}

/**
 * Fetches a stored-response sentiment explanation for the active workspace.
 *
 * @param params Sentiment explanation client parameters.
 * @return Sentiment explanation response.
 * @throws Error when the API request fails.
 */
async function sentimentExplanation(params: SentimentExplanationParams): Promise<SentimentExplanation> {
  const response = await fetch(buildSentimentExplanationUrl(params), { headers: wsHeaders() });
  return json<SentimentExplanation>(response);
}

export const api = {
  auth: {
    session: () =>
      fetch("/api/session", { credentials: "same-origin" })
        .then(r => json<{ auth_required: boolean; authenticated: boolean }>(r)),
    login: (password: string) =>
      fetch("/api/login", {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      }).then(r => json<{ success: boolean }>(r)),
    logout: () =>
      fetch("/api/logout", { method: "POST", credentials: "same-origin" })
        .then(r => json<{ success: boolean }>(r)),
  },

  workspaces: {
    list: () => fetch("/api/workspaces").then(r => json<Workspace[]>(r)),
    create: (name: string, description?: string) =>
      fetch("/api/workspaces", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, description }),
      }).then(r => json<Workspace>(r)),
    update: (id: number, fields: { name?: string; description?: string; primary_brand_id?: number | null }) =>
      fetch(`/api/workspaces/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(fields),
      }).then(r => json<Workspace>(r)),
    delete: (id: number) =>
      fetch(`/api/workspaces/${id}`, { method: "DELETE" }).then(r => json<{ success: boolean }>(r)),
  },

  brands: {
    list: () => fetch("/api/brands", { headers: wsHeaders() }).then(r => json<Brand[]>(r)),
    create: (name: string, description?: string) =>
      fetch("/api/brands", {
        method: "POST",
        headers: wsJsonHeaders(),
        body: JSON.stringify({ name, description }),
      }).then(r => json<Brand>(r)),
    update: (id: number, fields: { name?: string; description?: string }) =>
      fetch(`/api/brands/${id}`, {
        method: "PUT",
        headers: wsJsonHeaders(),
        body: JSON.stringify(fields),
      }).then(r => json<Brand>(r)),
    delete: (id: number) =>
      fetch(`/api/brands/${id}`, { method: "DELETE", headers: wsHeaders() }).then(r => json<{ success: boolean }>(r)),
  },

  cohorts: {
    list: () => fetch("/api/cohorts", { headers: wsHeaders() }).then(r => json<BrandCohortWithMembers[]>(r)),
    create: (name: string, description?: string, brandIds?: number[], topics?: string[]) =>
      fetch("/api/cohorts", {
        method: "POST",
        headers: wsJsonHeaders(),
        body: JSON.stringify({ name, description, brand_ids: brandIds, topics }),
      }).then(r => json<BrandCohortWithMembers>(r)),
    update: (id: number, fields: { name?: string; description?: string | null; topics?: string[] | null }) =>
      fetch(`/api/cohorts/${id}`, {
        method: "PUT",
        headers: wsJsonHeaders(),
        body: JSON.stringify(fields),
      }).then(r => json<BrandCohort>(r)),
    delete: (id: number) =>
      fetch(`/api/cohorts/${id}`, { method: "DELETE", headers: wsHeaders() }).then(r => json<{ success: boolean }>(r)),
    setMembers: (id: number, brandIds: number[]) =>
      fetch(`/api/cohorts/${id}/members`, {
        method: "PUT",
        headers: wsJsonHeaders(),
        body: JSON.stringify({ brand_ids: brandIds }),
      }).then(r => json<BrandCohortWithMembers>(r)),
    trackingStatus: () =>
      fetch("/api/cohorts/track-brand-health", { headers: wsHeaders() }).then(r => json<TrackingStatus>(r)),
    trackBrandHealth: (params: { cohort_ids?: number[]; models?: string[]; hour_utc?: number; day_of_week?: number } = {}) =>
      fetch("/api/cohorts/track-brand-health", {
        method: "POST",
        headers: wsJsonHeaders(),
        body: JSON.stringify(params),
      }).then(r => json<BrandHealthTracking & { already_tracking: boolean }>(r)),
  },

  plan: {
    get: () => fetch("/api/plan", { headers: wsHeaders() }).then(r => json<TrackingPlan>(r)),
    apply: (params: { topics: { name: string; blind?: boolean }[]; models?: string[]; cohort_ids?: number[]; cadence?: "weekly" | "daily" | "times_per_day"; times_per_day?: number; hour_utc?: number; day_of_week?: number }) =>
      fetch("/api/plan", { method: "POST", headers: wsJsonHeaders(), body: JSON.stringify(params) })
        .then(r => json<{ plan: TrackingPlan; created: string[]; replaced: string[]; disabled: string[]; unchanged: string[]; blind_enabled: string[]; blind_disabled: string[] }>(r)),
    estimate: (params: { topics: { name: string; blind?: boolean }[]; models?: string[]; cohort_ids?: number[]; cadence?: "weekly" | "daily" | "times_per_day"; times_per_day?: number }) =>
      fetch("/api/plan/estimate", { method: "POST", headers: wsJsonHeaders(), body: JSON.stringify(params) })
        .then(r => json<TrackingPlanEstimate>(r)),
  },

  topicSummaries: {
    list: (params: { category?: string; topic_id?: number }) => {
      const url = new URL("/api/topic-summaries", window.location.origin);
      if (params.topic_id !== undefined) url.searchParams.set("topic_id", String(params.topic_id));
      if (params.category !== undefined) url.searchParams.set("category", params.category);
      return fetch(url.pathname + url.search, { headers: wsHeaders() }).then(r => json<TopicSummary[]>(r));
    },
    generate: (params: { category?: string; topic_id?: number; model?: string; months?: number }) =>
      fetch("/api/topic-summaries", { method: "POST", headers: wsJsonHeaders(), body: JSON.stringify(params) })
        .then(r => json<TopicSummary & { cached: boolean }>(r)),
  },

  topics: {
    list: () => fetch("/api/topics", { headers: wsHeaders() }).then(r => json<TopicListItem[]>(r)),
    create: (params: { name: string; description?: string; kind?: TopicKind }) =>
      fetch("/api/topics", { method: "POST", headers: wsJsonHeaders(), body: JSON.stringify(params) })
        .then(r => json<Topic>(r)),
    get: (id: number) => fetch(`/api/topics/${id}`, { headers: wsHeaders() }).then(r => json<ResolvedTopicWithEstimate>(r)),
    overview: (id: number, params: { months?: number; days?: number; model?: string; subtopic_id?: number; bucket?: MetricBucket } = {}) => {
      const url = new URL(`/api/topics/${id}/overview`, window.location.origin);
      if (params.months !== undefined) url.searchParams.set("months", String(params.months));
      if (params.days !== undefined) url.searchParams.set("days", String(params.days));
      if (params.bucket) url.searchParams.set("bucket", params.bucket);
      if (params.model) url.searchParams.set("model", params.model);
      if (params.subtopic_id !== undefined) url.searchParams.set("subtopic_id", String(params.subtopic_id));
      return fetch(url.pathname + url.search, { headers: wsHeaders() }).then(r => json<TopicOverviewData>(r));
    },
    update: (id: number, fields: { name?: string; description?: string }) =>
      fetch(`/api/topics/${id}`, { method: "PATCH", headers: wsJsonHeaders(), body: JSON.stringify(fields) })
        .then(r => json<Topic>(r)),
    archive: (id: number) =>
      fetch(`/api/topics/${id}/archive`, { method: "POST", headers: wsHeaders() }).then(r => json<Topic>(r)),
    merge: (id: number, sourceId: number) =>
      fetch(`/api/topics/${id}/merge`, { method: "POST", headers: wsJsonHeaders(), body: JSON.stringify({ source_topic_id: sourceId }) })
        .then(r => json<{ target: Topic; source: Topic; apply: ApplyTopicResultWithEstimate | null }>(r)),
    setTracking: (id: number, cfg: {
      enabled: boolean;
      field_enabled?: boolean;
      blind_enabled: boolean;
      cadence: string | null;
      hour_utc?: number | null;
      day_of_week?: number | null;
      day_of_month?: number | null;
      times_per_day?: number | null;
      models: string[];
    }) =>
      fetch(`/api/topics/${id}/tracking`, { method: "PUT", headers: wsJsonHeaders(), body: JSON.stringify(cfg) })
        .then(r => json<ApplyTopicResultWithEstimate>(r)),
    estimate: (id: number, params: { models?: string[]; cadence?: string; times_per_day?: number; field_enabled?: boolean; blind_enabled?: boolean } = {}) => {
      const url = new URL(`/api/topics/${id}/estimate`, window.location.origin);
      if (params.models && params.models.length) url.searchParams.set("models", params.models.join(","));
      if (params.cadence) url.searchParams.set("cadence", params.cadence);
      if (params.times_per_day !== undefined) url.searchParams.set("times_per_day", String(params.times_per_day));
      if (params.field_enabled !== undefined) url.searchParams.set("field_enabled", String(params.field_enabled));
      if (params.blind_enabled !== undefined) url.searchParams.set("blind_enabled", String(params.blind_enabled));
      return fetch(url.pathname + url.search, { headers: wsHeaders() }).then(r => json<TopicEstimate>(r));
    },
    setCompetitors: (id: number, brandIds: number[]) =>
      fetch(`/api/topics/${id}/competitors`, { method: "PUT", headers: wsJsonHeaders(), body: JSON.stringify({ brand_ids: brandIds }) })
        .then(r => json<TopicCompetitorBrand[]>(r)),
    addCompetitor: (id: number, brandId: number) =>
      fetch(`/api/topics/${id}/competitors`, { method: "POST", headers: wsJsonHeaders(), body: JSON.stringify({ brand_id: brandId }) })
        .then(r => json<TopicCompetitorBrand[]>(r)),
    removeCompetitor: (id: number, brandId: number) =>
      fetch(`/api/topics/${id}/competitors/${brandId}`, { method: "DELETE", headers: wsHeaders() })
        .then(r => json<{ success: boolean }>(r)),
    addSubtopic: (id: number, name: string) =>
      fetch(`/api/topics/${id}/subtopics`, { method: "POST", headers: wsJsonHeaders(), body: JSON.stringify({ name }) })
        .then(r => json<Subtopic>(r)),
    renameSubtopic: (subtopicId: number, name: string) =>
      fetch(`/api/subtopics/${subtopicId}`, { method: "PATCH", headers: wsJsonHeaders(), body: JSON.stringify({ name }) })
        .then(r => json<Subtopic>(r)),
    removeSubtopic: (subtopicId: number) =>
      fetch(`/api/subtopics/${subtopicId}`, { method: "DELETE", headers: wsHeaders() })
        .then(r => json<{ success: boolean }>(r)),
    promoteSubtopic: (subtopicId: number) =>
      fetch(`/api/subtopics/${subtopicId}/promote`, { method: "POST", headers: wsHeaders() })
        .then(r => json<Topic>(r)),
    adoptStudy: (id: number, studyId: number) =>
      fetch(`/api/topics/${id}/adopt-study`, { method: "POST", headers: wsJsonHeaders(), body: JSON.stringify({ study_id: studyId }) })
        .then(r => json<Partial<ApplyTopicResultWithEstimate> & { adopted: boolean; applied: boolean; remaining: number; review_required: boolean }>(r)),
    adoptAllStudies: (id: number) =>
      fetch(`/api/topics/${id}/adopt-studies`, { method: "POST", headers: wsHeaders() })
        .then(r => json<{ adopted: number; apply: ApplyTopicResultWithEstimate | null; review_required: boolean }>(r)),
  },

  feed: {
    recent: (params: { days?: number; limit?: number } = {}) => {
      const url = new URL("/api/feed", window.location.origin);
      if (params.days) url.searchParams.set("days", String(params.days));
      if (params.limit) url.searchParams.set("limit", String(params.limit));
      return fetch(url.pathname + url.search, { headers: wsHeaders() }).then(r => json<FeedItem[]>(r));
    },
  },

  snapshots: {
    list: () => fetch("/api/snapshots", { headers: wsHeaders() }).then(r => json<SnapshotReportSummary[]>(r)),
    create: (params: { name?: string; months?: number; bucket?: "week" | "month"; cohort_ids?: number[]; prompt_id?: number; prompt_ids?: number[]; model?: string; scope_cohort_id?: number } = {}) =>
      fetch("/api/snapshots", { method: "POST", headers: wsJsonHeaders(), body: JSON.stringify(params) })
        .then(r => json<SnapshotReportSummary & { public_path: string }>(r)),
    delete: (id: number) =>
      fetch(`/api/snapshots/${id}`, { method: "DELETE", headers: wsHeaders() }).then(r => json<{ success: boolean }>(r)),
  },

  prompts: {
    list: () => fetch("/api/prompts", { headers: wsHeaders() }).then(r => json<Prompt[]>(r)),
    /**
     * Fetches the prompt CSV export for the active workspace.
     *
     * @return Promise resolving to the CSV blob.
     * @throws Error when the export request fails.
     */
    exportCsv: () => fetch("/api/export/prompts.csv", { headers: wsHeaders() }).then(blob),
    create: (name: string, template: string, dimensions: string[], type: PromptType = 'scoring', category?: string) =>
      fetch("/api/prompts", {
        method: "POST",
        headers: wsJsonHeaders(),
        body: JSON.stringify({ name, template, dimensions, type, category }),
      }).then(r => json<Prompt>(r)),
    update: (id: number, fields: { name?: string; template?: string; dimensions?: string[]; type?: PromptType; category?: string; subtopic_id?: number | null; mode?: PromptMode }) =>
      fetch(`/api/prompts/${id}`, {
        method: "PUT",
        headers: wsJsonHeaders(),
        body: JSON.stringify(fields),
      }).then(r => json<Prompt>(r)),
    delete: (id: number) =>
      fetch(`/api/prompts/${id}`, { method: "DELETE", headers: wsHeaders() }).then(r => json<{ success: boolean; mode: "deleted" | "archived" }>(r)),
  },

  models: {
    list: () => fetch("/api/models").then(r => json<Model[]>(r)),
  },

  comparisons: {
    list: (options?: { limit?: number; offset?: number }) => {
      const params = new URLSearchParams();
      params.set("limit", String(options?.limit ?? 200));
      if (options?.offset) params.set("offset", String(options.offset));
      return fetch(`/api/comparisons?${params}`, { headers: wsHeaders() }).then(r => json<ComparisonListItem[]>(r));
    },
    get: (id: number) => fetch(`/api/comparisons/${id}`, { headers: wsHeaders() }).then(r => json<ComparisonResult>(r)),
    getRich: (id: number, options?: { debug?: boolean }) => {
      const debugQuery = options?.debug ? "?debug=1" : "";
      return fetch(`/api/comparisons/${id}/rich${debugQuery}`, { headers: wsHeaders() }).then(r => json<CompareResultView>(r));
    },
    run: (params: { brand_ids: number[]; prompt_id: number; model: string; repeat?: number; query_mode?: QueryMode }) =>
      fetch("/api/comparisons", {
        method: "POST",
        headers: wsJsonHeaders(),
        body: JSON.stringify(params),
      }).then(r => json<RunComparisonResponse>(r)),
    runBatch: (params: { brand_ids: number[]; prompt_ids: number[]; models: string[]; query_mode?: QueryMode }) =>
      fetch("/api/batch-comparisons", {
        method: "POST",
        headers: wsJsonHeaders(),
        body: JSON.stringify(params),
      }).then(r => json<Comparison[]>(r)),
  },

  studies: {
    list: () => fetch("/api/studies", { headers: wsHeaders() }).then(r => json<Study[]>(r)),
    /**
     * Fetches the study-list CSV export for the active workspace.
     *
     * @return Promise resolving to the CSV blob.
     * @throws Error when the export request fails.
     */
    exportListCsv: () => fetch("/api/export/studies.csv", { headers: wsHeaders() }).then(blob),
    get: (id: number) => fetch(`/api/studies/${id}`, { headers: wsHeaders() }).then(r => json<StudyDetail>(r)),
    /**
     * Fetches the study run-history CSV export for the active workspace.
     *
     * @param id Study id to export.
     * @return Promise resolving to the CSV blob.
     * @throws Error when the export request fails.
     */
    exportCsv: (id: number) => fetch(`/api/studies/${id}/export.csv`, { headers: wsHeaders() }).then(blob),
    create: (params: { name: string; type: StudyType; brand_ids: number[]; prompt_id: number; model?: string; models?: string[] }) =>
      fetch("/api/studies", {
        method: "POST",
        headers: wsJsonHeaders(),
        body: JSON.stringify(params),
      }).then(r => json<Study>(r)),
    duplicate: (id: number, params: Partial<{ name: string; type: StudyType; brand_ids: number[]; prompt_id: number; model: string; models: string[]; tracked_brand_source: "manual" | "workspace_all" }>) =>
      fetch(`/api/studies/${id}/duplicate`, {
        method: "POST",
        headers: wsJsonHeaders(),
        body: JSON.stringify(params),
      }).then(r => json<Study>(r)),
    delete: (id: number) =>
      fetch(`/api/studies/${id}`, { method: "DELETE", headers: wsHeaders() }).then(r => json<{ success: boolean }>(r)),
    run: (id: number) =>
      fetch(`/api/studies/${id}/run`, { method: "POST", headers: wsHeaders() }).then(r => json<{ run_id: number; run_number: number }>(r)),
    getRun: (runId: number) =>
      fetch(`/api/study-runs/${runId}`, { headers: wsHeaders() }).then(r => json<StudyRun>(r)),
    diffs: (id: number) =>
      fetch(`/api/studies/${id}/diffs`, { headers: wsHeaders() }).then(r => json<RunDiff[]>(r)),
    explainRun: (runId: number) =>
      fetch(`/api/study-runs/${runId}/explain`, { method: "POST", headers: wsHeaders() }).then(r => json<{ explanation: string }>(r)),
  },

  alerts: {
    rules: () => fetch("/api/alert-rules", { headers: wsHeaders() }).then(r => json<AlertRule[]>(r)),
    createRule: (params: { brand_id: number; metric: AlertMetric; condition: AlertCondition; threshold: number; webhook_url?: string; cooldown_hours?: number }) =>
      fetch("/api/alert-rules", {
        method: "POST",
        headers: wsJsonHeaders(),
        body: JSON.stringify(params),
      }).then(r => json<AlertRule>(r)),
    updateRule: (id: number, fields: { metric?: AlertMetric; condition?: AlertCondition; threshold?: number; webhook_url?: string | null; enabled?: boolean; cooldown_hours?: number }) =>
      fetch(`/api/alert-rules/${id}`, {
        method: "PATCH",
        headers: wsJsonHeaders(),
        body: JSON.stringify(fields),
      }).then(r => json<AlertRule>(r)),
    deleteRule: (id: number) =>
      fetch(`/api/alert-rules/${id}`, { method: "DELETE", headers: wsHeaders() }).then(r => json<{ success: boolean }>(r)),
    events: (unackedOnly?: boolean) =>
      fetch(`/api/alerts${unackedOnly ? "?unacked=1" : ""}`, { headers: wsHeaders() }).then(r => json<AlertEvent[]>(r)),
    unackedCount: () =>
      fetch("/api/alerts/unacked-count", { headers: wsHeaders() }).then(r => json<{ count: number }>(r)),
    ack: (id: number) =>
      fetch(`/api/alerts/${id}/ack`, { method: "POST", headers: wsHeaders() }).then(r => json<{ success: boolean }>(r)),
    ackAll: () =>
      fetch("/api/alerts/ack-all", { method: "POST", headers: wsHeaders() }).then(r => json<{ acknowledged: number }>(r)),
  },

  reports: {
    list: () => fetch("/api/reports", { headers: wsHeaders() }).then(r => json<Report[]>(r)),
    get: (id: number) => fetch(`/api/reports/${id}`, { headers: wsHeaders() }).then(r => json<ReportDetail>(r)),
    create: (params: { name: string; brand_ids: number[]; prompt_id: number; models: string[]; runs_per_model: number; query_mode?: QueryMode }) =>
      fetch("/api/reports", {
        method: "POST",
        headers: wsJsonHeaders(),
        body: JSON.stringify(params),
      }).then(r => json<Report>(r)),
    delete: (id: number) =>
      fetch(`/api/reports/${id}`, { method: "DELETE", headers: wsHeaders() }).then(r => json<{ success: boolean }>(r)),
  },

  tournaments: {
    list: () => fetch("/api/tournaments", { headers: wsHeaders() }).then(r => json<Tournament[]>(r)),
    get: (id: number) => fetch(`/api/tournaments/${id}`, { headers: wsHeaders() }).then(r => json<TournamentResult>(r)),
    create: (params: { brand_ids: number[]; prompt_id: number; model: string }) =>
      fetch("/api/tournaments", {
        method: "POST",
        headers: wsJsonHeaders(),
        body: JSON.stringify(params),
      }).then(r => json<{ id: number; total_matchups: number }>(r)),
  },

  stats: {
    overall: () => fetch("/api/stats", { headers: wsHeaders() }).then(r => json<BrandStat[]>(r)),
    byPrompt: (promptId: number) =>
      fetch(`/api/stats/prompt/${promptId}`, { headers: wsHeaders() }).then(r => json<BrandStat[]>(r)),
  },

  runGroup: {
    get: (groupId: string) =>
      fetch(`/api/run-group/${groupId}`, { headers: wsHeaders() }).then(r => json<RunGroupResult[]>(r)),
  },

  rollups: {
    list: () => fetch("/api/rollups", { headers: wsHeaders() }).then(r => json<RollupReportSummary[]>(r)),
    get: (id: number) => fetch(`/api/rollups/${id}`, { headers: wsHeaders() }).then(r => json<RollupReport>(r)),
    create: (params: { period_type: RollupPeriodType; period_key: string; model?: string }) =>
      fetch("/api/rollups", {
        method: "POST",
        headers: wsJsonHeaders(),
        body: JSON.stringify(params),
      }).then(r => json<RollupReport>(r)),
    delete: (id: number) =>
      fetch(`/api/rollups/${id}`, { method: "DELETE", headers: wsHeaders() }).then(r => json<{ success: boolean }>(r)),
    autoSchedules: () =>
      fetch("/api/rollup-schedules", { headers: wsHeaders() }).then(r => json<RollupSchedule[]>(r)),
    setAutoSchedule: (periodType: RollupPeriodType, enabled: boolean) =>
      fetch("/api/rollup-schedules", {
        method: "PUT",
        headers: wsJsonHeaders(),
        body: JSON.stringify({ period_type: periodType, enabled }),
      }).then(r => json<RollupSchedule>(r)),
  },

  schedules: {
    list: () => fetch("/api/schedules", { headers: wsHeaders() }).then(r => json<ScheduleWithStudy[]>(r)),
    create: (params: { study_id: number; cadence: ScheduleCadence; hour_utc?: number; day_of_week?: number; day_of_month?: number; times_per_day?: number }) =>
      fetch("/api/schedules", {
        method: "POST",
        headers: wsJsonHeaders(),
        body: JSON.stringify(params),
      }).then(r => json<ScheduleWithStudy>(r)),
    update: (id: number, fields: { cadence?: ScheduleCadence; hour_utc?: number; day_of_week?: number; day_of_month?: number; times_per_day?: number; enabled?: boolean }) =>
      fetch(`/api/schedules/${id}`, {
        method: "PATCH",
        headers: wsJsonHeaders(),
        body: JSON.stringify(fields),
      }).then(r => json<ScheduleWithStudy>(r)),
    delete: (id: number) =>
      fetch(`/api/schedules/${id}`, { method: "DELETE", headers: wsHeaders() }).then(r => json<{ success: boolean }>(r)),
  },

  metrics: {
    sentimentExplanation,
    timeseries: (params: { metric: string; bucket?: MetricBucket; brand_id?: number; brand_name?: string; model?: string; prompt_id?: number; from?: string; to?: string }) => {
      const url = new URL("/api/metrics/timeseries", window.location.origin);
      for (const [key, value] of Object.entries(params)) {
        if (value !== undefined) url.searchParams.set(key, String(value));
      }
      return fetch(url, { headers: wsHeaders() }).then(r => json<MetricPoint[]>(r));
    },
    dashboard: (params: { metric: string; bucket?: MetricBucket; days?: number; model?: string; prompt_id?: number }) => {
      const url = new URL("/api/metrics/dashboard", window.location.origin);
      for (const [key, value] of Object.entries(params)) {
        if (value !== undefined) url.searchParams.set(key, String(value));
      }
      return fetch(url, { headers: wsHeaders() }).then(r => json<DashboardData>(r));
    },
    topicSnapshot: (params: { months?: number; cohort_ids?: number[]; model?: string } = {}) => {
      const url = new URL("/api/metrics/topic-snapshot", window.location.origin);
      if (params.months) url.searchParams.set("months", String(params.months));
      if (params.cohort_ids && params.cohort_ids.length) url.searchParams.set("cohort_ids", params.cohort_ids.join(","));
      if (params.model) url.searchParams.set("model", params.model);
      return fetch(url.pathname + url.search, { headers: wsHeaders() }).then(r => json<TopicSnapshotData>(r));
    },
    blindOverview: (params: { days?: number; model?: string; prompt_ids?: number[] } = {}) => {
      const url = new URL("/api/metrics/blind-overview", window.location.origin);
      if (params.days) url.searchParams.set("days", String(params.days));
      if (params.model) url.searchParams.set("model", params.model);
      if (params.prompt_ids && params.prompt_ids.length) url.searchParams.set("prompt_ids", params.prompt_ids.join(","));
      return fetch(url.pathname + url.search, { headers: wsHeaders() }).then(r => json<BlindOverviewData>(r));
    },
    topicDetail: (params: { category?: string; topic_id?: number; months?: number; model?: string; prompt_ids?: number[] }) => {
      const url = new URL("/api/metrics/topic-detail", window.location.origin);
      if (params.topic_id !== undefined) url.searchParams.set("topic_id", String(params.topic_id));
      if (params.category !== undefined) url.searchParams.set("category", params.category);
      if (params.months) url.searchParams.set("months", String(params.months));
      if (params.model) url.searchParams.set("model", params.model);
      if (params.prompt_ids && params.prompt_ids.length) url.searchParams.set("prompt_ids", params.prompt_ids.join(","));
      return fetch(url.pathname + url.search, { headers: wsHeaders() }).then(r => json<TopicDetailData>(r));
    },
    cohortSnapshot: (params: { metric?: string; months?: number; bucket?: "week" | "month"; cohort_ids?: number[]; model?: string; prompt_id?: number; prompt_ids?: number[]; scope_cohort_id?: number } = {}) => {
      const url = new URL("/api/metrics/cohort-snapshot", window.location.origin);
      if (params.metric) url.searchParams.set("metric", params.metric);
      if (params.months !== undefined) url.searchParams.set("months", String(params.months));
      if (params.bucket) url.searchParams.set("bucket", params.bucket);
      if (params.scope_cohort_id !== undefined) url.searchParams.set("scope_cohort_id", String(params.scope_cohort_id));
      if (params.cohort_ids && params.cohort_ids.length) url.searchParams.set("cohort_ids", params.cohort_ids.join(","));
      if (params.model) url.searchParams.set("model", params.model);
      if (params.prompt_id !== undefined) url.searchParams.set("prompt_id", String(params.prompt_id));
      if (params.prompt_ids && params.prompt_ids.length) url.searchParams.set("prompt_ids", params.prompt_ids.join(","));
      return fetch(url, { headers: wsHeaders() }).then(r => json<CohortSnapshotData>(r));
    },
    summary: (params: { metric: string; period?: MetricBucket; brand_id?: number; brand_name?: string; model?: string; prompt_id?: number }) => {
      const url = new URL("/api/metrics/summary", window.location.origin);
      for (const [key, value] of Object.entries(params)) {
        if (value !== undefined) url.searchParams.set(key, String(value));
      }
      return fetch(url, { headers: wsHeaders() }).then(r => json<MetricSummary>(r));
    },
  },

  trends: {
    get: (brandId: number, params?: { model?: string; prompt_id?: number }) => {
      const url = new URL(`/api/trends/${brandId}`, window.location.origin);
      if (params?.model) url.searchParams.set("model", params.model);
      if (params?.prompt_id) url.searchParams.set("prompt_id", String(params.prompt_id));
      return fetch(url, { headers: wsHeaders() }).then(r => json<TrendEntry[]>(r));
    },
  },
};
